import * as React from 'react';
import { useAuth } from './auth';
import { DEFAULT_LANGUAGE, normalizeLanguage, type SupportedLanguage } from './languages';
import en from '@/locales/en';
import cs from '@/locales/cs';
import de from '@/locales/de';
import es from '@/locales/es';
import fr from '@/locales/fr';
import hr from '@/locales/hr';
import hu from '@/locales/hu';
import id from '@/locales/id';
import it from '@/locales/it';
import ja from '@/locales/ja';
import ro from '@/locales/ro';
import sk from '@/locales/sk';
import sl from '@/locales/sl';
import vi from '@/locales/vi';

export type TranslationKey = keyof typeof en;
type MessageValues = Record<string, string | number>;
type Catalog = Partial<Record<TranslationKey, string>>;

const catalogs: Record<SupportedLanguage, Catalog> = {
  en,
  cs,
  de,
  es,
  fr,
  hr,
  hu,
  id,
  it,
  ja,
  ro,
  sk,
  sl,
  vi,
};

interface I18nContextValue {
  language: SupportedLanguage;
  t: (key: TranslationKey, values?: MessageValues) => string;
  /**
   * Translate a data-driven label (status, priority, source, system field).
   * Returns `fallback` unchanged when no catalog entry exists for `key`.
   */
  tOr: (key: string, fallback: string) => string;
}

const defaultContext: I18nContextValue = {
  language: DEFAULT_LANGUAGE,
  t: (key, values) => interpolate(resolveTranslation(en, key), values),
  tOr: (key, fallback) => lookupOptional(en, key, fallback),
};

const I18nContext = React.createContext<I18nContextValue>(defaultContext);

function interpolate(message: string, values?: MessageValues): string {
  if (!values) return message;
  return message.replace(/\{(\w+)\}/g, (placeholder, key) => (
    values[key] === undefined ? placeholder : String(values[key])
  ));
}

function lookupOptional(catalog: Catalog, key: string, fallback: string): string {
  const localized = (catalog as Record<string, string | undefined>)[key];
  if (typeof localized === 'string' && localized.trim() !== '') return localized;
  const english = (en as Record<string, string>)[key];
  return english ?? fallback;
}

export function resolveTranslation(catalog: Catalog, key: TranslationKey): string {
  const localized = catalog[key];
  const message = typeof localized === 'string' && localized.trim() !== ''
    ? localized
    : en[key];
  return message ?? key;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Pre-login pages and the customer portal have no agent language, so fall
  // back to the browser language (English when unsupported or unavailable).
  const browserLanguage = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const language = normalizeLanguage(user?.language ?? browserLanguage);

  React.useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = React.useMemo<I18nContextValue>(() => ({
    language,
    t: (key, values) => interpolate(resolveTranslation(catalogs[language], key), values),
    tOr: (key, fallback) => lookupOptional(catalogs[language], key, fallback),
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return React.useContext(I18nContext);
}
